var Component  = require("enyo/Component"),
    History    = window.history,
    dispatcher = require("enyo/dispatcher"),
    kind       = require("enyo/kind");

module.exports = kind(/** @lends app.core.Router.prototype */ {

    /**
     * @private
     */
    name: "app.core.Router",

    /**
     * @private
     */
    kind: Component,

    /**
     * The root path of the route
     *
     * @type {?string}
     * @public
     */
    root: null,

    /**
     * Router will redirect to this path if no routes match the current
     * pathname.
     *
     * @type {string|function}
     * @private
     */
    _defaultPath: null,

    /**
     * The registered routes.
     *
     * @type {Object}
     * @public
     */
    routes: null,

    /**
     * The last pathname for which a handler was fired.
     *
     * @type  {?string}
     * @readonly
     * @public
     */
    matchedPathname: null,

    /**
     * The last route for which a handler was fired.
     *
     * @type  {?string}
     * @readonly
     * @public
     */
    matchedRoute: null,

    /**
     * The parameters extracted from the currently matched pathname.
     *
     * @type  {array}
     * @readonly
     * @public
     */
    matchedParams: [],

    /**
     * Whether the next route to be handled should skip the cache when
     * navigating forward or backward through browsing history.
     *
     * @type {boolean}
     * @private
     */
    skipCache: false,

    /**
     * Path to skip over when navigating forward or backward through
     * browsing history.
     *
     * @type {?string}
     * @private
     */
    skipPathname: null,

    /**
     * The direction in which we're currently navigating through the browsing
     * history. Used internally in conjunction with `skipPathname`.
     *
     * @type {?string}
     * @private
     */
    direction: null,

    // Initialization
    // --------------------------------

    /**
     * @method
     * @private
     */
    constructed: kind.inherit(function(sup) {
        return function() {
            this.routes     = {};
            this.onPopstate = this.bindSafely(this.onPopstate, this);
            sup.apply(this, arguments);
        };
    }),

    /**
     * @method
     * @private
     */
    create: kind.inherit(function(sup) {
        return function() {
            sup.apply(this, arguments);
            dispatcher.listen(window, "popstate", this.onPopstate);
        };
    }),

    // Routing
    // --------------------------------

    /**
     * Register a route and it's handler
     *
     * @param   {Object}    route    The route definition
     * @param   {Function}  handler  The handler for this route
     * @returns {Object}    The callee, for chaining
     * @public
     */
    addRoute: function(route) {
        var pattern = route.pattern.toString();

        this.routes[pattern] = route;

        return this;
    },

    /**
     * Unregister a route.
     *
     * @param   {RegExp|string}  pattern  The pattern for this route
     * @returns {Object}         The callee, for chaining
     * @public
     */
    removeRoute: function(pattern) {
        if (pattern instanceof RegExp) {
            pattern = pattern.toString();
        }

        delete this.routes[pattern];

        return this;
    },

    /**
     * Prepend root onto navigation path
     *
     * @param  {string}  path  The path to prefix with root url
     * @return {string}  The prefixed path
     * @private
     */
    prependRoot: function(path) {
        return (this.get("root") || "") + path;
    },

    /**
     * Remove root path from navigation path
     *
     * @param  {string}  path  The path to prefix with root url
     * @return {string}  The prefixed path
     * @private
     */
    stripRoot: function(path) {
        var prefix = this.get("root") || "";

        if (path.substring(0, prefix.length) === prefix) {
            return path.substring(prefix.length);
        }
        else {
            return path;
        }
    },

    /**
     * Handle current pathname.
     *
     * @private
     */
    onPopstate: function() {
        var skipCache    = this.skipCache,
            skipPathname = this.skipPathname,
            direction    = this.direction;

        // These flags are single-use
        this.skipCache    = null;
        this.skipPathname = null;
        this.direction    = null;

        // When navigating forward or backward, we might want to skip over paths
        // that are duplicates of the current pathname.
        if (skipPathname && this.getCurrentPathname() === skipPathname) {
            if (direction === "back") {
                this.back(true, skipCache);
            }
            else if (direction === "forward") {
                this.forward(true, skipCache);
            }
        }

        // No skipping necessary
        else {
            this.handle(this.getCurrentPathname(), skipCache);
        }
    },

    /**
     * Get the current pathname.
     *
     * @return  {string}  The current pathname
     * @public
     */
    getCurrentPathname: function() {
        return this.stripRoot(window.location.pathname);
    },

    /**
     * Get the current search string.
     *
     * @return  {string}  The current search string
     * @public
     */
    getCurrentSearch: function() {
        return window.location.search;
    },

    /**
     * Get the current path.
     *
     * @return  {string}  The current path
     * @public
     */
    getCurrentPath: function() {
        return this.getCurrentPathname() + this.getCurrentSearch();
    },

    /**
     * @param  {string|function}  defaultPath  The default path
     * @public
     */
    setDefaultPath: function(defaultPath) {
        this._defaultPath = defaultPath;
    },

    /**
     * @param   {Object=}  params  The params
     * @return  {string}   The path
     * @public
     */
    getDefaultPath: function(params) {
        var defaultPath = this._defaultPath;

        switch (typeof defaultPath) {
            case "function":
                return defaultPath(params || {});
            case "string":
                return defaultPath;
            default:
                return "/";
        }
    },

    /**
     * Test the supplied pathname against the registered routes. Fires handler
     * for 1st route that matches. Handler will receive matched pathname,
     * parameters extracted from the pathname by the route's regex and the
     * search string.
     *
     * The search string will *not* be evaluated by the router, it only gets
     * converted to a URLSearchParams object and passed to the handler.
     *
     * @param {string}   pathname   The pathname to evaluate
     * @param {boolean}  skipCache  Whether to skip the cache
     * @public
     */
    handle: function(pathname, skipCache) {
        var routeHandler = this.get("routeHandler"),
            routes       = this.routes,
            defaultPath  = this.getDefaultPath(),
            i,
            route,
            match,
            params,
            handler;

        for (i in routes) {
            if (i && routes.hasOwnProperty(i)) {
                route = this.routes[i];
                match = pathname.match(route.pattern);

                if (match) {
                    match.shift();
                    params = Array.prototype.slice.call(match);

                    // Cancel the previous handler if it isn't done yet.
                    if (routeHandler && !routeHandler.finished) {
                        routeHandler.finish();
                    }

                    // Register new handler
                    handler = new route.Handler();
                    this.set("matchedPathname", pathname);
                    this.set("matchedRoute", route);
                    this.set("matchedParams", params);
                    this.set("routeHandler", handler);
                    handler.handle(
                        this.app,
                        pathname,
                        params,
                        new URLSearchParams(this.getCurrentSearch()),
                        skipCache
                    );

                    return;
                }
            }
        }

        // No match, redirect to default path if defined
        defaultPath && this.redirect(defaultPath);
    },

    // Navigation
    // --------------------------------

    /**
     * Start the router by handling the current pathname
     *
     * @public
     */
    start: function() {
        this.handle(this.getCurrentPathname());
    },

    /**
     * Navigate to a new path, pushing it onto the history stack
     *
     * @param  {string}   path       The path to navigate to
     * @param  {boolean}  skipCache  Whether the handler should refrain from
     *                               using cached data.
     * @public
     */
    navigate: function(path, skipCache) {
        this.skipCache    = null;
        this.skipPathname = null;
        this.direction    = null;
        History.pushState(null, null, this.prependRoot(path));
        this.handle(this.getCurrentPathname(), Boolean(skipCache));
    },

    /**
     * Navigate to a new path, but force the browser to reload from scratch
     * while doing so.
     *
     * @param  {string}  path  The path to navigate to
     * @public
     */
    assign: function(path) {
        window.location.assign(this.prependRoot(path));
    },

    /**
     * Refresh the page, but force the browser to reload from scratch while
     * doing so.
     *
     * @public
     */
    reload: function() {
        window.location.reload();
    },

    /**
     * Redirect to a new path, replacing the current state in the history stack
     *
     * @param  {string}   path       The path to navigate to
     * @param  {boolean}  skipCache  Whether the handler should refrain from
     *                               using cached data.
     * @public
     */
    redirect: function(path, skipCache) {
        this.skipCache    = null;
        this.skipPathname = null;
        this.direction    = null;
        History.replaceState(null, null, this.prependRoot(path));
        this.handle(this.getCurrentPathname(), Boolean(skipCache));
    },

    /**
     * Go back to previous history state. If no previous state is available,
     * redirect to default path instead.
     *
     * @param  {?boolean}  enforceNewPathname  Whether the router should keep
     *                                         going back until a pathname
     *                                         different from the current one is
     *                                         encountered. Defaults to false.
     * @param  {boolean}  skipCache            Whether the handler should
     *                                         refrain from using cached data.
     * @public
     */
    back: function(enforceNewPathname, skipCache) {
        var defaultPath = this.getDefaultPath();

        this.skipCache = Boolean(skipCache);

        if (enforceNewPathname) {
            this.skipPathname = this.getCurrentPathname();
            this.direction    = "back";
        }
        else {
            this.skipPathname = null;
            this.direction    = null;
        }

        if (History.length > 1) {
            History.go(-1);
        }
        else {
            defaultPath && this.redirect(defaultPath, skipCache);
        }
    },

    /**
     * Go forward to next history state
     *
     * @param  {?boolean}  enforceNewPathname  Whether the router should keep
     *                                         going forward until a pathname
     *                                         different from the current one is
     *                                         encountered. Defaults to false.
     * @param  {boolean}  skipCache            Whether the handler should
     *                                         refrain from using cached data.
     * @public
     */
    forward: function(enforceNewPathname, skipCache) {
        this.skipCache = Boolean(skipCache);

        if (enforceNewPathname !== undefined) {
            this.skipPathname = this.getCurrentPathname();
            this.direction    = "forward";
        }
        else {
            this.skipPathname = null;
            this.direction    = null;
        }
        History.go(1);
    },

    /**
     * Just fire current route again
     *
     * @public
     */
    refresh: function() {
        this.skipCache    = null;
        this.skipPathname = null;
        this.direction    = null;
        this.redirect(this.getCurrentPath(), true);
    }

});
